import React, { useMemo, useState } from 'react';
import StudentsTable from '../students/StudentsTable';
import EmptyState from '../common/EmptyState';
import DrilldownHeader from './DrilldownHeader';
import { MixLegend } from './cells';
import { engagedRanking } from '../../lib/derive';
import { ENGAGED_COLUMNS, ENGAGED_SORT, nextSort, sortStudents } from '../../lib/studentTable';
import { studentsCsv, csvFilename } from '../../lib/studentsCsv';
import { downloadCsv } from '../../lib/downloadCsv';
import { windowCaption } from '../../lib/format';

export const PAGE_SIZE = 50;

const countText = (n) => `${n.toLocaleString()} ${n === 1 ? 'student' : 'students'} with completed work`;

/**
 * Most engaged in full (mockup state 2): every student of the Students list
 * rows with at least one item in the period, ranked exactly as the
 * Overview's card ranks them (lib/derive engagedRanking), 50 to a page.
 * The export is the whole list in the order on screen.
 */
const EngagedDrilldown = ({ roster, loading, error, scopeLabel, period, onBack, onOpenStudent }) => {
  const [sort, setSort] = useState(ENGAGED_SORT);
  const [page, setPage] = useState(0);

  const ranked = useMemo(() => engagedRanking(roster), [roster]);
  const sorted = useMemo(() => sortStudents(ranked, sort, ENGAGED_COLUMNS), [ranked, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const shown = sorted.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  const changeSort = (key) => {
    setSort((prev) => nextSort(prev, key, ENGAGED_COLUMNS));
    setPage(0);
  };

  return (
    <div className="td-students td-drill">
      <DrilldownHeader
        title="Most engaged"
        subtitle={[loading ? null : countText(ranked.length), scopeLabel, windowCaption(period)].filter(Boolean).join(' · ')}
        onBack={onBack}
        exportDisabled={!sorted.length}
        onExport={() => downloadCsv(csvFilename(`${scopeLabel} most engaged`), studentsCsv(sorted, ENGAGED_COLUMNS))}
      />
      {error ? <div className="td-error">{error}</div> : null}
      {loading ? <p className="td-loading">Loading…</p> : null}
      {!loading && !error && sorted.length === 0 ? (
        <EmptyState title="No activity yet" body="Students appear here once they finish some work in this period." />
      ) : null}
      {!loading && sorted.length > 0 ? (
        <>
          <MixLegend />
          <StudentsTable
            label="Most engaged"
            students={shown}
            columns={ENGAGED_COLUMNS}
            selectable={false}
            sort={sort}
            onSort={changeSort}
            onOpenStudent={onOpenStudent}
          />
          {pages > 1 ? (
            <nav className="td-pager" aria-label="Most engaged pages">
              <button type="button" className="td-btn td-btn--small" disabled={current === 0} onClick={() => setPage(current - 1)}>
                Previous
              </button>
              <span className="td-pager__status">
                {`${current * PAGE_SIZE + 1}–${current * PAGE_SIZE + shown.length} of ${sorted.length}`}
              </span>
              <button type="button" className="td-btn td-btn--small" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                Next
              </button>
            </nav>
          ) : null}
          <p className="td-card__note">
            Ranked as on the Overview: most items first, then most active days, then name. Items are
            completed pieces of work in the selected period.
          </p>
        </>
      ) : null}
    </div>
  );
};

export default EngagedDrilldown;
